/** Global Imports */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@material-ui/lab/Autocomplete';

/** Component Imports */
import { styles } from './styles';
import { PrimaryButton, SecondaryButton } from 'components/common/buttons';

/** Context Imports */
import { userContext } from 'context/user';
import { useNotification } from 'context/notification';
import { useLoader } from 'context/loader';

/** Service Imports */
import { addProgram } from 'services/programs';
import { initiateProgramRepository } from 'services/programs';
import { Divider } from '@material-ui/core';
import { rules } from '../../../../util/rule-name-convention';
import DOMPurify from 'dompurify';
import useApplicationPermission from 'components/common/hooks/useCollabPermission';

export default function AddProgram({
  isOpen,
  closePopup,
  allPrograms,
  productPlatforms,
  businessUnits,
  navigateToProgramDetails,
}) {
  const classes = styles();
  const { dispatchLoader } = useLoader();
  const { dispatchNotification } = useNotification();
  const { currentUser } = useContext(userContext);
  const { userId, firstName, lastName } = currentUser;
  const [open, setOpen] = useState(isOpen);
  const [selectedProgramName, setSelectedProgramName] = useState('');
  const [selectedProductPlatform, setSelectedProductPlatform] = useState('');
  const [selectedBusinessUnit, setSelectedbusinessUnit] = useState('');
  const [programsNameList, setProgramsNameList] = useState([]);
  const [errorProgramName, setErrorProgramName] = useState();
  const [isAdvanceOption, setIsAdvanceOption] = useState(false);
  const [isRepoInitiated, setIsRepoInitiated] = useState(false);

  const handleClose = () => {
    setErrorProgramName(null);
    setSelectedProgramName('');
    setSelectedProductPlatform('');
    setSelectedbusinessUnit('');
    setOpen(false);
    setIsAdvanceOption(false);
    setIsRepoInitiated(false);
    closePopup();
  };

  const { collabPlatformAdminRole } = useApplicationPermission();
  const isCollabPlatfromAdmin = useMemo(
    () => collabPlatformAdminRole !== undefined,
  );

  const handleProgramNameChange = (e) => {
    const newProgramName = e.target.value.trim();
    const sanitizedName = DOMPurify.sanitize(newProgramName);
    const errors = [];
    for (const rule of rules) {
      if (!rule.isSatisfied(sanitizedName)) {
        errors.push(rule.desc);
      }
    }

    if (programsNameList.includes(sanitizedName.toLowerCase())) {
      errors.push('Program name already exists');
    }
    // Update error state if any validation fails
    if (errors.length > 0) {
      setErrorProgramName(errors.join(' ')); // Combine all error messages
    } else {
      setErrorProgramName(null);
    }
    setSelectedProgramName(sanitizedName);
  };

  const handleProductPlatformChange = (e, selectedOption) => {
    setSelectedProductPlatform(selectedOption || '');
  };

  const handleBusinessUnitChange = (e, selectedOption) => {
    setSelectedbusinessUnit(selectedOption || '');
  };

  const handleAddProgramClick = (e) => {
    let isValidated = e.currentTarget.form.reportValidity();
    if (isValidated && !errorProgramName) {
      let newProgramDetails = {
        programName: selectedProgramName,
        productPlatformId: selectedProductPlatform.id,
        businessUnitId: selectedBusinessUnit.id,
        userId: currentUser.userId,
        isGitProgram: isRepoInitiated,
      };
      dispatchLoader({ show: true, message: 'Creating the program..' });
      addProgramInCollab(newProgramDetails);
    }
  };
  const initiateGitProgram = (gitProgramDetails) => {
    initiateProgramRepository(gitProgramDetails).then((res) => {
      dispatchLoader({ show: false });
      if (res.git_program && res.repository_url !== undefined) {
        dispatchNotification({
          show: true,
          severity: 'success',
          message: `Successfully generated Git Repo for this program ${res.repository_url}`,
        });
      } else {
        if (gitProgramDetails.git_program) {
          dispatchNotification({
            show: true,
            severity: 'error',
            message: `Failed to initiate the program Git Repository. Please @ support.IPMCloud@carrier.com `,
          });
        }
      }
      navigateToProgramDetails({
        programId: res.id,
        ownerId: userId,
        ownerFirstName: firstName,
        ownerLastName: lastName,
        ownerName: `${firstName} ${lastName}`,
      });
    });
  };

  const addProgramInCollab = (newProgramDetails) => {
    addProgram(newProgramDetails).then((res) => {
      if (res.status === 'success') {
        handleClose();
        const gitProgramDetails = {
          id: res.programId,
          name: newProgramDetails.programName,
          git_program: isRepoInitiated,
        };
        if (isRepoInitiated) {
          dispatchLoader({
            show: true,
            message: 'Creating the Git Repository..',
          });
        }
        initiateGitProgram(gitProgramDetails);
      } else {
        dispatchLoader({ show: false });
        dispatchNotification({
          show: true,
          severity: 'error',
          message: `Failed to create the program. Error: ${res.error_message}`,
        });
      }
    });
  };

  const handleToggle = () => {
    setIsAdvanceOption(!isAdvanceOption);
  };

  const handleCheckboxChange = (event) => {
    setIsRepoInitiated(event.target.checked);
  };

  const createGitOptionUI = () => {
    return (
      <>
        {isCollabPlatfromAdmin && (
          <Button
            variant="text"
            size="small"
            color="primary"
            sx={{ textTransform: 'capitalize' }}
            onClick={handleToggle}
          >
            Advance Option
          </Button>
        )}
        {isAdvanceOption && (
          <Box sx={{ marginTop: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isRepoInitiated}
                  onChange={handleCheckboxChange}
                  name="githubRepo"
                />
              }
              label="Initiate GitHub Repository"
            />
          </Box>
        )}
        {isRepoInitiated && (
          <Box
            sx={{
              marginTop: 2,
              padding: 2,
              borderTop: '1px solid red',
              borderRadius: 1,
            }}
          >
            <Typography color="error" variant="caption">
              Enable this option to automatically create a GitHub repository for
              this program to execute your regression tests outside IPM Cloud.
              <br></br>
              <b>Note: This is a Beta feature.</b>
            </Typography>
          </Box>
        )}
      </>
    );
  };

  useEffect(() => {
    setProgramsNameList(allPrograms.map((ap) => ap.programName.toLowerCase()));
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle disableTypography>New program</DialogTitle>
      <Divider />
      <form className={classes.root} noValidate>
        <DialogContent>
          <div className={classes.dialogContent}>
            <TextField
              required
              className={classes.dialogInputField}
              placeholderTextColor="#152C73 !important"
              label="Program Name"
              style={{ width: '100%' }}
              variant="outlined"
              size="small"
              onChange={handleProgramNameChange}
              error={errorProgramName}
            />
            {errorProgramName && (
              <p className={classes.programNameInputValidation}>
                {errorProgramName}
              </p>
            )}
            <Autocomplete
              value={selectedProductPlatform}
              getOptionLabel={(option) => option.name}
              onChange={(e, val) => handleProductPlatformChange(e, val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.dialogInputField}
                  required
                  label="Product Platform"
                  variant="outlined"
                  size="small"
                />
              )}
              options={productPlatforms}
            />
            <Autocomplete
              getOptionLabel={(option) => option.name}
              value={selectedBusinessUnit}
              onChange={(e, val) => handleBusinessUnitChange(e, val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.dialogInputField}
                  required
                  label="Business Unit"
                  variant="outlined"
                  size="small"
                />
              )}
              options={businessUnits}
            />
            {createGitOptionUI()}
          </div>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
          <PrimaryButton
            btnStyle={errorProgramName ? 'disabled' : 'primary'}
            onClick={handleAddProgramClick}
          >
            Create Program
          </PrimaryButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
